import { useMemo } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import { KernelBlock } from "../kernelPrimitives/index.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useQuery } from "../../lib/dataClient.js";
import { signalDefByCommand, useSignalCatalog } from "../../lib/signalCatalog.js";
import { Chip } from "../detailPrimitives.jsx";
import { findingLabel, kindSummary, rollupFindingKinds, writerShort } from "../../lib/configShadowFindings.js";
import EndpointConfigStatePanel from "./EndpointConfigStatePanel.jsx";

const GHOST_BTN = "inline-flex min-h-[24px] items-center cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[9px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]";

// Kinds shown per row before the rest collapse behind a count. A signal declares
// a small fixed kind set, so this is a backstop against a future one with a wide
// vocabulary rather than a limit today's data reaches — the cell's unbounded axis
// was targets, and rolling to kind is what removed it.
const ROW_KIND_LIMIT = 4;

// One-line summary of an endpoint's newest grouped finding: the layer that won,
// the process that wrote it, and what it did.
function summarize(group) {
  if (!group) return "";
  const writer = group.writers?.length ? writerShort(group.writers[0]) : "";
  return [group.layer, writer, group.operations?.[0]].filter(Boolean).join(" · ");
}

// Tenant-wide kind breakdown, in the prose line under the metrics. This is the
// bounded form of what the table cells used to try to say per row: which problems
// are actually in this range, and how much of the volume each one accounts for.
function kindBreakdown(kinds) {
  return kinds
    .map((k) => `${findingLabel(k.tag)} (${k.targets.length} target${k.targets.length === 1 ? "" : "s"}, ${k.observations} observation${k.observations === 1 ? "" : "s"})`)
    .join("; ");
}

function fmtWhen(ms) {
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  return `${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}\n${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

const CELL = "px-[8px] py-[6px] align-top text-[11px]";
const HEAD = "px-[8px] py-[5px] text-left text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]";

// Per-signal lens for an ENDPOINT-SUBJECT signal — the endpoints that fired it,
// rather than the prompts. Selected by the `when` predicate on the value.signals
// lens registration (see appLenses.js); prompt-subject signals keep
// SignalDetailBody.
//
// Why a separate body rather than a mode inside SignalDetailBody: every read
// differs. An endpoint-subject tag is computed from an endpoint event with no
// prompt row, so getTraces / getSignalFiringSeries return nothing here, and the
// row identity is an endpoint id instead of a trace id — which also means the
// right panel shows that endpoint's config rather than a prompt transcript.
export default function EndpointSignalDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const command = (terminal?.dim === "signals" || terminal?.dim === "signal")
    ? (terminal.ids?.[0] ?? terminal.id ?? null)
    : null;
  const { ready, denied, error } = useSignalCatalog();
  const def = useMemo(() => (command && ready ? signalDefByCommand(command) : null), [command, ready]);
  const resolved = !!def;
  const disabled = resolved && !def.deployedEnabled;

  const { data: firings, loading } = useQuery(
    "getSignalEndpointFirings",
    { scopePath, namespace: def?.namespace, range: ctx?.range },
    { enabled: resolved && !!def?.namespace },
  );

  const endpoints = firings?.endpoints || [];
  const series = firings?.series || [];
  const hasFiring = series.some((v) => v > 0);
  const readFailed = firings ? firings.ok === false : false;
  const unscopedDims = firings?.unscopedDims || [];
  // Three quantities, never interchangeable: distinct problems, machines
  // affected, and raw observations. One condition can be observed many times, so
  // observations run well ahead of findings.
  const findingCount = firings?.groupedTotal ?? 0;
  const endpointCount = firings?.endpointCount ?? endpoints.length;
  const observations = firings?.total ?? 0;

  // Per-endpoint kind rollups, plus the tenant-wide one for the summary line.
  // Derived here rather than in the read so the read stays the raw-row contract
  // the chart and the panel also draw on.
  const kindsByEndpoint = useMemo(() => {
    const m = new Map();
    for (const ep of endpoints) m.set(ep.endpointId, rollupFindingKinds(ep.groupedFindings || []));
    return m;
  }, [endpoints]);
  const allKinds = useMemo(
    () => rollupFindingKinds(endpoints.flatMap((ep) => ep.groupedFindings || [])),
    [endpoints],
  );

  const showDefinition = () => { ctx?.onShowDefinition?.(); slot?.onExpandDetail?.(); };

  const openEndpoint = (ep) => {
    ctx?.onOpenDetail?.({
      eyebrow: "Endpoint config",
      title: ep.hostname || ep.endpointId,
      detailKey: `epcfg:${ep.endpointId}`,
      content: (
        <EndpointConfigStatePanel
          endpointId={ep.endpointId}
          hostname={ep.hostname}
          findings={ep.groupedFindings || []}
          range={ctx?.range}
        />
      ),
    });
    slot?.onExpandDetail?.();
  };

  if (!resolved) {
    const title = denied || error ? "Signals unavailable" : !ready ? "Loading…" : "Unknown signal";
    const note = denied
      ? "You don’t have permission to view signals."
      : error
        ? "Couldn’t load signals. Try again shortly."
        : !ready
          ? "Loading the signal registry…"
          : "This signal isn’t in the catalog for this tenant.";
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]" aria-busy={!ready && !denied && !error}>
        <PageHero eyebrow="Signal" title={title} detail={command ? `/${command}` : ""} />
        <span className={NOTE_CLASS}>{note}</span>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]" aria-busy={loading}>
      <PageHero
        eyebrow="Signal"
        title={def.label}
        detail={def.description || `/${def.command}`}
        controls={
          <button type="button" onClick={showDefinition} className={GHOST_BTN}>
            View definition
          </button>
        }
        // Three quantities, each under its own label, none of them "findings" —
        // for a signal that re-targets on every write the target count is not a
        // problem count, and the kind count is the one an analyst can act on.
        metrics={[
          { label: "Finding kinds", value: String(allKinds.length), note: "distinct problems" },
          { label: "Config targets", value: String(findingCount), note: `across ${endpointCount} endpoint${endpointCount === 1 ? "" : "s"}` },
          { label: "Observations", value: String(observations), note: "raw firings in range" },
        ]}
      />
      <div className="flex flex-wrap gap-[5px]">
        <Chip>/{def.command}</Chip>
        <Chip>{def.namespace}</Chip>
        <Chip>endpoint</Chip>
        <Chip>{disabled ? "disabled" : "built-in"}</Chip>
      </div>
      {disabled ? (
        <span className={NOTE_CLASS}>
          This signal is disabled. No new findings will appear, but historical findings remain visible until they expire.
        </span>
      ) : null}
      {readFailed ? (
        <span className={NOTE_CLASS}>
          Couldn’t read endpoint findings for this signal. Endpoint-scoped roles can’t query
          endpoint-subject signals yet. This view requires a tenant-wide reader.
        </span>
      ) : (
        <span className={NOTE_CLASS}>
          {allKinds.length} finding kind{allKinds.length === 1 ? "" : "s"} over {findingCount} config
          target{findingCount === 1 ? "" : "s"} on {endpointCount} endpoint
          {endpointCount === 1 ? "" : "s"}
          {observations > findingCount
            ? `, from ${observations} observations. A finding may be observed more than once.`
            : ""}
          {allKinds.length ? `. ${kindBreakdown(allKinds)}` : ""}
          {unscopedDims.length
            ? `. Tenant-wide: the current ${unscopedDims.join(" / ")} scope doesn’t narrow endpoint findings.`
            : "."}
        </span>
      )}
      {hasFiring ? (
        <KernelBlock spec={{ kind: "chart", mode: "line", layers: [{ label: "Observations", series }], height: 150, range: ctx?.range, title: "Observations", compactStandingTooltip: true }} />
      ) : null}
      {endpoints.length ? (
        <table className="w-full border-collapse" data-testid="endpoint-findings">
          <thead>
            <tr className="border-b border-[color:var(--line-soft)]">
              <th className={HEAD}>Last fired</th>
              <th className={HEAD}>Endpoint</th>
              <th className={HEAD}>Findings</th>
              <th className={HEAD}>Newest</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep) => {
              const groups = ep.groupedFindings || [];
              const kinds = kindsByEndpoint.get(ep.endpointId) || [];
              const shownKinds = kinds.slice(0, ROW_KIND_LIMIT);
              const hiddenKinds = kinds.length - shownKinds.length;
              return (
                <tr
                  key={ep.endpointId}
                  onClick={() => openEndpoint(ep)}
                  className="cursor-pointer border-b border-[color:var(--line-soft)] hover:bg-[color:var(--hover)]"
                  data-endpoint-id={ep.endpointId}
                >
                  <td className={`${CELL} whitespace-pre-line text-[color:var(--muted)]`}>{fmtWhen(ep.lastFiredMs)}</td>
                  <td className={`${CELL} text-[color:var(--ink)]`}>{ep.hostname || ep.endpointId.slice(0, 8)}</td>
                  {/* One badge per finding KIND, never per target. The target
                      count rides inside the badge because targets are the axis
                      that can run to the hundreds on a single endpoint. */}
                  <td className={CELL}>
                    <span className="flex flex-wrap gap-[4px]" data-testid="finding-kinds">
                      {shownKinds.map((k) => (
                        <Chip key={k.tag} title={k.targets[0]?.sourcePath}>
                          {kindSummary(k)}
                        </Chip>
                      ))}
                      {hiddenKinds > 0 ? <Chip>+{hiddenKinds} more</Chip> : null}
                    </span>
                  </td>
                  <td className={`${CELL} font-mono text-[10px] text-[color:var(--muted)]`}>{summarize(groups[0])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <span className={NOTE_CLASS}>
          {loading ? "Reading endpoint findings…" : readFailed ? "" : "No endpoints fired this signal in this scope."}
        </span>
      )}
    </ScrollArea>
  );
}
